import './PermissionDropdown.scss';
import { ReactElement, useLayoutEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdPeopleOutline } from 'react-icons/md';
import { applyCeiling, ResolvedRoleLevels } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Button } from '@Components/Button/Button';
import { Dropdown, MenuItem } from '@Components/Dropdown';
import { Appearance, Design, Placement, Size, StretchMode } from '@Components/designConstants';
import { useAppSelector } from '@src/redux';
import { roleSelector } from '@src/redux/user';
import { IconButton } from '@src/Components/IconButton/IconButton';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { assignableRoleLevels, roleLevelLabel } from './chart-permissions';

// One entity for both surfaces. `page` already says whether this was a chart or a dashboard, so
// spelling 'chart_permissions' here would contradict itself on every dashboard change.
const PERMISSIONS_ENTITY = 'permissions';

const levelOptions = (levels: number[]): MenuItem<string>[] =>
  levels.map(level => ({ value: String(level), label: roleLevelLabel(level) }));

interface PermissionDropdownProps {
  // The record's STORED levels, as two plain numbers rather than the record itself: `applyCeiling`
  // takes two numbers, and a `DashboardConfig` carries them optionally — so the narrowing belongs at
  // the call site, in the branch that already chose this control over the View only badge and
  // therefore already knows the record has loaded.
  viewRoleRequired: number;
  editRoleRequired: number;
  // Which page this control is sitting on, for the Mixpanel event — a constant here would mislabel
  // every dashboard change as a chart change. Callers pass a `PAGE_NAMES` value.
  page: string;
  // Open the dialog from OUTSIDE, seeded with this pair instead of the record's stored one. Set by
  // a chat-driven request that would let MORE people see the record: the user confirms it on the
  // control they already know, rather than meeting a bespoke dialog. `undefined` leaves the dialog
  // under its own trigger button's control.
  openWith?: ResolvedRoleLevels;
  // Called when a dialog opened that way closes, either cancelled or saved, so the caller can drop
  // the pair it asked for. Without it a cancelled confirmation would re-open on the next render.
  onExternalClose?: () => void;
  // Persist the new pair. MUST REJECT if the save failed, and must report the failure to the user
  // itself (`useSetError`): on a rejection this control keeps the dialog open with the user's
  // selection intact so they can retry, and does not track a save that did not happen.
  onSave: (levels: ResolvedRoleLevels) => Promise<void>;
}

/**
 * Who may see, and who may change, one chart or dashboard.
 *
 * A trigger button naming the current audience, and a dialog holding the two columns.
 *
 * THE DIALOG FORM IS THE POINT, and it was chosen over a two-column dropdown for one reason: local
 * state. A dropdown that hands over both columns only on Apply never sees a selection before then,
 * so it must build its edit column from the STORED view level. A user who narrows the audience and
 * widens edit rights in one visit would then have their edit level silently lowered by
 * `applyCeiling` — a correct save that nobody was warned about. Here the edit column rebuilds as the
 * view selection changes, so the impossible option is never offered at all.
 *
 * It is a react-bootstrap `Modal` rather than `AlertDialog`, and that is deliberate too:
 * `AlertDialog` calls `onHide()` synchronously right after `primaryAction.onClick()`, so for an async save
 * its `loading` prop can never render, and a refused save — a 403 is an expected outcome of the very
 * gate this feature added — would close the dialog and discard what the user had picked. This
 * follows `SaveChartAsNewDialog`, the sibling dialog on the same surfaces.
 *
 * DRAWS NOTHING when this user has no second level to assign — see the gate below. That is not an
 * edge case: it is every external (role 10) user with edit rights on a record.
 */
export const PermissionDropdown = ({
  viewRoleRequired,
  editRoleRequired,
  page,
  openWith,
  onExternalClose,
  onSave
}: PermissionDropdownProps): ReactElement | undefined => {
  const userRole = useAppSelector(roleSelector);
  const [showLocally, setShowLocally] = useState(false);
  const [saving, setSaving] = useState(false);
  // Open when the trigger was pressed OR when a caller handed us a pair to confirm.
  const show = showLocally || openWith !== undefined;

  // Closing has to clear BOTH sources. Clearing only the local flag would leave `openWith` set and
  // the dialog would re-open on the very next render.
  const close = () => {
    setShowLocally(false);
    onExternalClose?.();
  };

  // The stored pair drawn through the ceiling BEFORE it seeds anything. A row written before this
  // rule existed can hold an edit level wider than its view level, and the control has to show the
  // value a save would actually store rather than the one the row happens to hold.
  const stored = applyCeiling({ viewRoleRequired, editRoleRequired });
  const [view, setView] = useState(stored.viewRoleRequired);
  const [edit, setEdit] = useState(stored.editRoleRequired);

  // Re-seed on each open, so a cancelled visit leaves nothing behind and a record changed elsewhere
  // shows its current pair rather than the one this component last rendered. `openWith` wins when
  // present: the point of an external open is to show what was ASKED for, not what is stored.
  //
  // `useLayoutEffect`, not `useEffect`: an external open flips `show` in the same render that hands
  // us `openWith`, so a post-paint effect would show the STORED pair for one frame before snapping
  // to the requested one — a visible wrong answer in the dialog whose only job is to show which
  // audience is about to be chosen.
  useLayoutEffect(() => {
    if (!show) return;
    setView(openWith?.viewRoleRequired ?? stored.viewRoleRequired);
    setEdit(openWith?.editRoleRequired ?? stored.editRoleRequired);
  }, [
    show,
    openWith?.viewRoleRequired,
    openWith?.editRoleRequired,
    stored.viewRoleRequired,
    stored.editRoleRequired
  ]);

  // The ceiling applied to the LIVE selection, derived rather than written back into state. An edit
  // level wider than the picked view level cannot be stored, so narrowing the audience has to carry
  // the edit level down with it — and deriving means there is no render in which the two disagree.
  const effectiveEdit = Math.min(edit, view);

  // The edit column's third argument is the CEILING, and it is the picked view level, not the stored
  // one. `effectiveEdit` rather than the stored edit level is passed as `current` so the value on
  // display is always one of the options offered.
  //
  // `assignableRoleLevels` rather than bpm-types' `assignableLevels`: the rule still decides what is
  // legal, and this product then offers three of the five tiers (see chart-permissions.ts).
  const viewLevels = assignableRoleLevels(userRole, stored.viewRoleRequired);
  const editLevels = assignableRoleLevels(userRole, effectiveEdit, view);

  // NOTHING TO OFFER, SO OFFER NOTHING. The list never runs below the caller's own role — a user
  // cannot build a record they could not then open — so an EXTERNAL (role 10) user gets exactly one
  // option in each column: 10, which is the level the record must already hold for them to have
  // reached this control at all. The dialog would open onto two dropdowns with a single unchangeable
  // option each.
  //
  // Narrowing the offered set to three tiers widened who this covers. A role-2 or role-3 user now
  // has no VIEW level to pick either, since the only offered level at or above their role is 10 —
  // so they keep the control only while their record's edit level is still something they can move
  // off, and lose it once both columns settle. That falls out of a middle tier of "Tinuiti internal
  // only" (level 1), which is a level those roles cannot see themselves.
  //
  // Withheld rather than disabled, which is how this feature treats every control a user cannot
  // use (see the delete control in DashboardsList). A greyed button explains nothing, and a
  // disabled <button> emits no pointer events, so a tooltip on it would never fire either. The
  // trigger's label is no loss: it can only ever read "Everyone with access to this client" for
  // these users, since that is the only audience a record they can edit is allowed to have.
  //
  // Phrased as "has a choice" rather than "is role 10" deliberately. It stays correct for the
  // off-scale roles real accounts carry — role 4 gets [4, 10] and keeps the control — and it cannot
  // flip mid-interaction: a single-option view column means `view` can never change, which leaves
  // the edit column fixed too.
  //
  // ⚠️ AN EXTERNAL OPEN MUST NEVER REACH THIS RETURN. A caller handing us `openWith` for a user with
  // nothing to assign would get no dialog and no message, and their request would vanish. It cannot
  // happen: `decideSharingRequest` refuses an unassignable audience before it can ask for a confirm
  // (see its own note, and the test named for it). Do not soften that ordering.
  if (viewLevels.length <= 1 && editLevels.length <= 1) return undefined;

  const handleSave = async () => {
    setSaving(true);
    const saved = await onSave({ viewRoleRequired: view, editRoleRequired: effectiveEdit })
      .then(() => true)
      .catch(() => false);
    setSaving(false);
    // The caller has already surfaced the failure. Leave the dialog open on the user's selection.
    if (!saved) return;
    Mixpanel.track(MxE.RecordSaved, { page, entity: PERMISSIONS_ENTITY });
    close();
  };

  return (
    <>
      <IconButton
        design={Design.NEUTRAL}
        size={Size.MD}
        tooltip={{
          text: `View permissions: ${roleLevelLabel(stored.viewRoleRequired)}. Click to change permissions.`,
          placement: Placement.BOTTOM
        }}
        onClick={() => setShowLocally(true)}
      >
        <MdPeopleOutline />
      </IconButton>
      <Modal
        show={show}
        onHide={close}
        contentClassName='permission-dropdown-dialog'
        centered
        backdrop
        keyboard
      >
        <Modal.Body>
          <h4 className='dialog-title'>Who can see this</h4>
          <div className='dialog-field'>
            <Dropdown
              ariaLabel='Who can see this'
              stretch={StretchMode.FILL}
              value={String(view)}
              options={levelOptions(viewLevels)}
              onChange={next => setView(Number(next))}
            />
          </div>
          <h4 className='dialog-title'>Who can edit this</h4>
          <div className='dialog-field'>
            <Dropdown
              ariaLabel='Who can edit this'
              stretch={StretchMode.FILL}
              value={String(effectiveEdit)}
              options={levelOptions(editLevels)}
              onChange={next => setEdit(Number(next))}
            />
            {/* Say it, because the list SHRINKING as the audience narrows is otherwise a surprise —
                the options simply stop appearing and nothing explains why. */}
            <span className='dialog-field-consequence'>
              Nobody can edit something they cannot see, so this list stops where the audience above
              does.
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={saving}
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            onClick={close}
          >
            Cancel
          </Button>
          <Button loading={saving} onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
